/**
 * "Medios del estímulo" — sección de la vista detalle del Banco de Palabras.
 * Gestiona la imagen (ilustración vectorial plana) y el audio humano (dicción
 * neutra es-PE de la palabra aislada) de un ítem del corpus. SOLO medios de
 * PRESENTACIÓN: no toca el corpus (regla de oro M1). Versionado + soft-delete
 * viven en el backend; aquí subimos, reemplazamos, borramos (lógico) y oímos.
 */
import { useEffect, useRef, useState } from "react";
import { adminApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { CorpusTipoMedia, WordMediaItem } from "../../api/types.js";
import { Async, fmtFecha } from "./ui.js";
import s from "./admin.module.css";

const MAX_MS = 10_000;

export function MediosEstimulo({ corpusTipo, corpusRef }: { corpusTipo: CorpusTipoMedia; corpusRef: string }) {
  const q = useQuery(() => adminApi.medios(corpusTipo, corpusRef), [corpusTipo, corpusRef]);
  const [error, setError] = useState<string | null>(null);

  async function subir(form: FormData) {
    setError(null);
    try {
      await adminApi.subirMedio(corpusTipo, corpusRef, form);
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }
  async function borrar(mediaId: string) {
    setError(null);
    try {
      await adminApi.eliminarMedio(corpusTipo, corpusRef, mediaId);
      q.reload();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className={`${s.panel} ${s.panelPad}`} style={{ marginTop: 12 }}>
      <div className={s.sectionTitle}>Medios del estímulo</div>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      <Async q={q}>
        {(medios) => (
          <div style={{ display: "grid", gap: 20 }}>
            <ImagenBloque
              activo={medios.imagen}
              historial={medios.historial.filter((h) => h.mediaTipo === "imagen")}
              onSubir={subir}
              onBorrar={borrar}
            />
            <AudioBloque
              activo={medios.audio}
              historial={medios.historial.filter((h) => h.mediaTipo === "audio")}
              onSubir={subir}
              onBorrar={borrar}
            />
          </div>
        )}
      </Async>
    </div>
  );
}

// ─────────────────────────────── Imagen ───────────────────────────────

function ImagenBloque({
  activo,
  historial,
  onSubir,
  onBorrar,
}: {
  activo: WordMediaItem | null;
  historial: WordMediaItem[];
  onSubir: (form: FormData) => Promise<void>;
  onBorrar: (mediaId: string) => Promise<void>;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function elegir(f: File | null) {
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : null);
  }
  async function confirmar() {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file, file.name);
    await onSubir(form);
    setBusy(false);
    elegir(null);
  }

  return (
    <div>
      <div className={s.dt}>Imagen {activo ? `(v${activo.version})` : ""}</div>
      <p className={s.muted} style={{ margin: "2px 0 8px" }}>
        Estilo acordado: ilustración vectorial plana. PNG/JPG/WEBP, máx. 2MB. (La generación por IA
        es otro proyecto; aquí solo subida manual.)
      </p>
      {activo ? (
        <div className={s.btnRow} style={{ alignItems: "flex-start" }}>
          <img
            src={activo.url}
            alt="estímulo"
            style={{ maxWidth: 160, maxHeight: 160, borderRadius: 8, border: "1px solid var(--line)" }}
          />
          <button className={`${s.btn} ${s.btnDanger}`} onClick={() => void onBorrar(activo.id)}>
            Eliminar
          </button>
        </div>
      ) : (
        <p className={s.muted}>Sin imagen activa.</p>
      )}

      <div className={s.toolbar} style={{ marginTop: 10 }}>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(e) => elegir(e.target.files?.[0] ?? null)}
        />
      </div>
      {preview ? (
        <div className={s.btnRow} style={{ marginTop: 8, alignItems: "flex-start" }}>
          <img src={preview} alt="preview" style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }} />
          <button className={`${s.btn} ${s.btnPrimary}`} disabled={busy} onClick={() => void confirmar()}>
            {activo ? "Reemplazar" : "Subir"}
          </button>
          <button className={s.btn} disabled={busy} onClick={() => elegir(null)}>
            Cancelar
          </button>
        </div>
      ) : null}

      <Historial items={historial} tipo="imagen" />
    </div>
  );
}

// ─────────────────────────────── Audio ────────────────────────────────

function AudioBloque({
  activo,
  historial,
  onSubir,
  onBorrar,
}: {
  activo: WordMediaItem | null;
  historial: WordMediaItem[];
  onSubir: (form: FormData) => Promise<void>;
  onBorrar: (mediaId: string) => Promise<void>;
}) {
  const [grabando, setGrabando] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [duracionMs, setDuracionMs] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef<number>(0);

  useEffect(() => () => detenerTimer(), []);

  function detenerTimer() {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }

  async function grabar() {
    setError(null);
    setBlob(null);
    setPreviewUrl(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const b = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        setDuracionMs(Math.min(Date.now() - startRef.current, MAX_MS));
        detenerTimer();
        setGrabando(false);
      };
      recRef.current = rec;
      startRef.current = Date.now();
      setElapsed(0);
      rec.start();
      setGrabando(true);
      timerRef.current = setInterval(() => {
        const e = Date.now() - startRef.current;
        setElapsed(e);
        if (e >= MAX_MS) detener();
      }, 100);
    } catch {
      setError("No se pudo acceder al micrófono. Revisa los permisos del navegador.");
    }
  }
  function detener() {
    recRef.current?.state === "recording" && recRef.current.stop();
  }

  async function guardar() {
    if (!blob) return;
    setBusy(true);
    const form = new FormData();
    form.append("duracionMs", String(duracionMs || 1)); // antes del archivo
    const ext = (blob.type.split("/")[1] ?? "webm").split(";")[0];
    form.append("file", blob, `estimulo.${ext}`);
    await onSubir(form);
    setBusy(false);
    setBlob(null);
    setPreviewUrl(null);
  }

  async function subirArchivo(f: File | null) {
    if (!f) return;
    setBusy(true);
    setError(null);
    // Duración de un archivo existente: medida con un <audio> temporal.
    const url = URL.createObjectURL(f);
    const dur = await duracionDe(url).catch(() => 0);
    URL.revokeObjectURL(url);
    const form = new FormData();
    form.append("duracionMs", String(Math.round(dur) || 1));
    form.append("file", f, f.name);
    await onSubir(form);
    setBusy(false);
  }

  return (
    <div>
      <div className={s.dt}>Audio {activo ? `(v${activo.version})` : ""}</div>
      <p className={s.muted} style={{ margin: "2px 0 8px" }}>
        Graba <b>la palabra aislada</b>, dicción neutra <b>es-PE</b> (para consistencia entre quienes
        graben). WEBM/OGG/MP3/WAV, máx. 5MB y <b>10&nbsp;s</b>.
      </p>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}

      {activo ? (
        <div className={s.btnRow} style={{ alignItems: "center" }}>
          <audio src={activo.url} controls preload="none" />
          <span className={s.muted}>{activo.duracionMs ? `${(activo.duracionMs / 1000).toFixed(1)}s` : ""}</span>
          <button className={`${s.btn} ${s.btnDanger}`} onClick={() => void onBorrar(activo.id)}>
            Eliminar
          </button>
        </div>
      ) : (
        <p className={s.muted}>Sin audio activo.</p>
      )}

      <div className={s.toolbar} style={{ marginTop: 10, gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {grabando ? (
          <button className={`${s.btn} ${s.btnDanger}`} onClick={detener}>
            ⏹ Detener ({(elapsed / 1000).toFixed(1)}s / 10s)
          </button>
        ) : (
          <button className={s.btn} onClick={() => void grabar()}>
            ⏺ Grabar
          </button>
        )}
        <span className={s.muted}>o</span>
        <input
          type="file"
          accept="audio/webm,audio/ogg,audio/mpeg,audio/mp3,audio/wav,audio/x-wav"
          onChange={(e) => void subirArchivo(e.target.files?.[0] ?? null)}
        />
      </div>

      {previewUrl ? (
        <div className={s.btnRow} style={{ marginTop: 8, alignItems: "center" }}>
          <audio src={previewUrl} controls />
          <span className={s.muted}>{(duracionMs / 1000).toFixed(1)}s</span>
          <button className={`${s.btn} ${s.btnPrimary}`} disabled={busy} onClick={() => void guardar()}>
            {activo ? "Reemplazar" : "Guardar"}
          </button>
          <button className={s.btn} disabled={busy} onClick={() => void grabar()}>
            Regrabar
          </button>
        </div>
      ) : null}

      <Historial items={historial} tipo="audio" />
    </div>
  );
}

function duracionDe(url: string): Promise<number> {
  return new Promise((resolve, reject) => {
    const a = document.createElement("audio");
    a.preload = "metadata";
    a.onloadedmetadata = () => resolve(Number.isFinite(a.duration) ? a.duration * 1000 : 0);
    a.onerror = () => reject(new Error("no metadata"));
    a.src = url;
  });
}

// ────────────────────────────── Historial ─────────────────────────────

function Historial({ items, tipo }: { items: WordMediaItem[]; tipo: "imagen" | "audio" }) {
  if (items.length === 0) return null;
  return (
    <details className={s.details} style={{ marginTop: 10 }}>
      <summary>Versiones anteriores ({items.length})</summary>
      <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
        {items.map((h) => (
          <div key={h.id} className={s.btnRow} style={{ alignItems: "center" }}>
            <span className={`${s.badge} ${s.badgeNeutral}`}>v{h.version}</span>
            {tipo === "imagen" ? (
              <img src={h.url} alt={`v${h.version}`} style={{ maxHeight: 48, borderRadius: 4 }} />
            ) : (
              <audio src={h.url} controls preload="none" style={{ height: 32 }} />
            )}
            <span className={s.muted}>
              {h.subidoPorNombre ?? "—"} · {fmtFecha(h.createdAt)}
            </span>
          </div>
        ))}
      </div>
    </details>
  );
}
